import { collection, doc, getDoc, getDocs, limit, orderBy, query, type Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { departmentIdScopeKey } from '../utils/normalizeIds';
import { safeStrForConsole } from '../utils/safeErrorLogging';

export interface FallIncidentRow {
  id?: string;
  eventAt: Timestamp | { toDate: () => Date };
  eventDateRaw?: string;
  fiscalQuarter: string;
  fiscalYear: number;
  eventYearMonth?: string;
  departmentId: string;
  wardRaw?: string;
  ipdOpd: string;
  severityLevel: string;
  flagA?: boolean;
  flagB?: boolean;
  flagC?: boolean;
  flagD?: boolean;
  flagE?: boolean;
  flagF?: boolean;
  flagG?: boolean;
  flagH?: boolean;
  flagI?: boolean;
  causeMain: string;
  causeSub: string;
  causeOther?: string;
  th?: string;
  htr?: string;
}

export interface FallIncidentMeta {
  totalRecordCount?: number;
  skippedUnmappedWard?: number;
  skippedInvalidDate?: number;
  skippedEmpty?: number;
  lastSyncAt?: { toDate: () => Date } | null;
  importSchemaVersion?: string;
}

/**
 * Future hook for process/compliance metrics from an external system.
 * Do not fabricate values — dashboard shows an explicit empty state when null.
 */
export type FallIncidentComplianceSnapshot = {
  asOf?: Date;
  metrics?: Record<string, number | null>;
  sourceLabelTh?: string;
};

export interface FallIncidentFetchOptions {
  /** Client-side filter only; Firestore rules allow all rows for any signed-in user. */
  departmentIds?: string[];
  fiscalYear?: number;
  fiscalQuarter?: string;
  severityLevel?: string;
  causeMain?: string;
  /** Max documents to read (default 500, cap 1000). */
  limit?: number;
}

export const sanitizeDepartmentIds = (ids?: readonly unknown[]) =>
  Array.from(
    new Set(
      (ids ?? [])
        .map((id) => (typeof id === 'string' ? id.trim() : safeStrForConsole(id).trim()))
        .filter(Boolean),
    ),
  );

const toStrField = (v: unknown): string =>
  typeof v === 'string' ? v : v && typeof (v as { id?: string }).id === 'string' ? (v as { id: string }).id : '';

export function fallIncidentRecordToRow(docId: string, d: Record<string, unknown>): FallIncidentRow {
  return {
    id: docId,
    eventAt: (d.eventAt as Timestamp) ?? (d.eventAt as { toDate: () => Date }),
    eventDateRaw: typeof d.eventDateRaw === 'string' ? d.eventDateRaw : undefined,
    fiscalQuarter: typeof d.fiscalQuarter === 'string' ? d.fiscalQuarter : '',
    fiscalYear: typeof d.fiscalYear === 'number' && Number.isFinite(d.fiscalYear) ? d.fiscalYear : 0,
    eventYearMonth: typeof d.eventYearMonth === 'string' ? d.eventYearMonth : undefined,
    departmentId: toStrField(d.departmentId),
    wardRaw: typeof d.wardRaw === 'string' ? d.wardRaw : undefined,
    ipdOpd: typeof d.ipdOpd === 'string' ? d.ipdOpd : '',
    severityLevel: typeof d.severityLevel === 'string' ? d.severityLevel : '',
    flagA: typeof d.flagA === 'boolean' ? d.flagA : undefined,
    flagB: typeof d.flagB === 'boolean' ? d.flagB : undefined,
    flagC: typeof d.flagC === 'boolean' ? d.flagC : undefined,
    flagD: typeof d.flagD === 'boolean' ? d.flagD : undefined,
    flagE: typeof d.flagE === 'boolean' ? d.flagE : undefined,
    flagF: typeof d.flagF === 'boolean' ? d.flagF : undefined,
    flagG: typeof d.flagG === 'boolean' ? d.flagG : undefined,
    flagH: typeof d.flagH === 'boolean' ? d.flagH : undefined,
    flagI: typeof d.flagI === 'boolean' ? d.flagI : undefined,
    causeMain: typeof d.causeMain === 'string' ? d.causeMain : '',
    causeSub: typeof d.causeSub === 'string' ? d.causeSub : '',
    causeOther: typeof d.causeOther === 'string' ? d.causeOther : undefined,
    th: typeof d.th === 'string' ? d.th : undefined,
    htr: typeof d.htr === 'string' ? d.htr : undefined,
  };
}

export async function fetchFallIncidentMeta(): Promise<FallIncidentMeta | null> {
  const metaSnap = await getDoc(doc(db, 'fallIncidentMeta', 'summary'));
  if (!metaSnap.exists()) return null;
  return metaSnap.data() as FallIncidentMeta;
}

/**
 * Load fall incidents (uniform Firestore read). Optional `departmentIds` filters in memory only.
 */
export async function fetchFallIncidentData(options: FallIncidentFetchOptions = {}): Promise<FallIncidentRow[]> {
  const coll = collection(db, 'fallIncidents');
  const cap = Math.min(Math.max(options.limit ?? 500, 1), 1000);
  const qy = query(coll, orderBy('eventAt', 'desc'), limit(cap));
  const snapshot = await getDocs(qy);
  const scoped = sanitizeDepartmentIds(options.departmentIds);
  const scopeSet =
    scoped.length && !scoped.includes('*')
      ? new Set(scoped.map((id) => departmentIdScopeKey(id)))
      : null;

  let rows = snapshot.docs.map((d) => fallIncidentRecordToRow(d.id, d.data() as Record<string, unknown>));

  if (scopeSet && scopeSet.size > 0) {
    rows = rows.filter((r) => scopeSet!.has(departmentIdScopeKey(r.departmentId)));
  }
  if (options.fiscalYear != null) {
    rows = rows.filter((r) => r.fiscalYear === options.fiscalYear);
  }
  if (options.fiscalQuarter) {
    rows = rows.filter((r) => r.fiscalQuarter === options.fiscalQuarter);
  }
  if (options.severityLevel) {
    rows = rows.filter((r) => r.severityLevel === options.severityLevel);
  }
  if (options.causeMain) {
    rows = rows.filter((r) => r.causeMain === options.causeMain);
  }
  return rows;
}

export interface FallIncidentAggregates {
  total: number;
  bySeverity: Record<string, number>;
  byDepartment: Record<string, number>;
  byCauseMain: Record<string, number>;
  byQuarter: Record<string, number>;
  byIpdOpd: Record<string, number>;
}

/** Align dashboard/export buckets: unknown values map to em dash, not arbitrary strings. */
function bucketIpdOpdForAggregate(raw: string | undefined | null): string {
  const s = (raw ?? '').trim().toUpperCase();
  if (!s) return '—';
  if (s === 'IPD' || s.includes('IPD')) return 'IPD';
  if (s === 'OPD' || s.includes('OPD')) return 'OPD';
  return '—';
}

export function computeFallIncidentAggregates(rows: FallIncidentRow[]): FallIncidentAggregates {
  const bySeverity: Record<string, number> = {};
  const byDepartment: Record<string, number> = {};
  const byCauseMain: Record<string, number> = {};
  const byQuarter: Record<string, number> = {};
  const byIpdOpd: Record<string, number> = {};

  rows.forEach((r) => {
    const sev = r.severityLevel || '—';
    bySeverity[sev] = (bySeverity[sev] ?? 0) + 1;
    const dept = r.departmentId || '—';
    byDepartment[dept] = (byDepartment[dept] ?? 0) + 1;
    const cause = r.causeMain || '—';
    byCauseMain[cause] = (byCauseMain[cause] ?? 0) + 1;
    const q = r.fiscalQuarter || '—';
    byQuarter[q] = (byQuarter[q] ?? 0) + 1;
    const io = bucketIpdOpdForAggregate(r.ipdOpd);
    byIpdOpd[io] = (byIpdOpd[io] ?? 0) + 1;
  });

  return {
    total: rows.length,
    bySeverity,
    byDepartment,
    byCauseMain,
    byQuarter,
    byIpdOpd,
  };
}
